import { supabase } from '../core/supabaseClient'
import { pollingManager } from '../core/pollingManager'
import { fieldState } from '@shared/stores/fieldScope'
import { eventBus } from '@shared/stores/globalState'
import { EventQueryBuilder } from './queryBuilder'
import { eventCacheService } from './cache'
import type { CachedEvent } from './cache'
import {
  EVENT_TYPES,
  EVENT_PRIORITIES,
  EVENT_LEVELS,
  EVENT_SOURCES,
  INCIDENT_LEVELS,
  INCIDENT_STATUSES,
  RISK_TYPES,
  RISK_LEVELS
} from './constants'
import {
  getEventTypeLabel,
  getPriorityLabel,
  getSourceLabel,
  getEventTypeColor,
  getPriorityColor,
  getIncidentLevelLabel,
  getIncidentStatusLabel,
  getRiskTypeLabel,
  getRiskLevelLabel
} from './helpers'
import {
  createStandardEvent as _createStandardEvent,
  convertEventFormat as _convertEventFormat,
  convertEventsToDisplayFormat,
  convertEventsToStandardFormat,
  transformMqttDataToEvent as _transformMqttDataToEvent,
  generateDefaultActions as _generateDefaultActions,
  mapEventTypeToFieldEventType as _mapEventTypeToFieldEventType,
  validateEventFormat as _validateEventFormat
} from './eventFormat'
import {
  createIncidentEvent as _createIncidentEvent,
  getIncidentEvents as _getIncidentEvents,
  getIncidentsByLevel as _getIncidentsByLevel,
  getIncidentsByStatus as _getIncidentsByStatus,
  updateIncidentStatus as _updateIncidentStatus,
  addIncidentResponseLog as _addIncidentResponseLog,
  createRiskEvent as _createRiskEvent,
  getRiskEvents as _getRiskEvents,
  getRiskEventsByType as _getRiskEventsByType,
  getRiskEventsByLevel as _getRiskEventsByLevel,
  getRiskStats as _getRiskStats,
  getIncidentStats as _getIncidentStats
} from './eventIncident'
import {
  getEventStats as _getEventStats,
  getEventTrend as _getEventTrend,
  getFieldEventRanking as _getFieldEventRanking,
  getTodayEventStats as _getTodayEventStats,
  getDashboardStats as _getDashboardStats,
  getDashboardStatsWithDurationFilter as _getDashboardStatsWithDurationFilter,
  groupBy as _groupBy,
  calculateTrend as _calculateTrend,
  calculateFieldRanking as _calculateFieldRanking,
  calculateFlowData as _calculateFlowData
} from './eventStats'
import {
  getUserFieldIds as _getUserFieldIds,
  loadEventsWithCache as _loadEventsWithCache,
  calculateSyncRange as _calculateSyncRange,
  backwardSync as _backwardSync,
  incrementalSync as _incrementalSync,
  firstSync as _firstSync
} from './eventSync'
import type {
  EventRow,
  EventType,
  EventPriority,
  EventLevel,
  EventSource,
  RiskType,
  RiskLevel,
  EventListener,
  NotificationStrategy,
  SyncProgressCallback,
  SyncInfo,
  LoadEventsCallbacks,
  SyncRange,
  SyncCacheMeta,
  EventFilters,
  EventDashboardResponse,
  DisplayEvent,
  UnreadCounts,
  ResponseLog,
  MqttData
} from './types'
// RealtimePostgresChangesPayload no longer needed (polling mode)

// Re-export 常數和輔助方法供外部使用
export * from './constants'
export * from './helpers'

// Re-export 從 service.ts 搬到 types.ts 的型別（向下相容）
export type {
  EventListener,
  NotificationStrategy,
  SyncProgressCallback,
  SyncInfo,
  LoadEventsCallbacks,
  SyncRange,
  EventFilters,
  DisplayEvent,
  UnreadCounts,
  ResponseLog,
  MqttData
} from './types'
export type { EventDashboardResponse } from './types'
/** @deprecated 使用 SyncCacheMeta */
export type { SyncCacheMeta as CacheMeta } from './types'
/** @deprecated 使用 EventDashboardResponse */
export type { EventDashboardResponse as DashboardStats } from './types'

// ============ 服務類別 ============

class EventService {
  isInitialized = false
  private _isStarting = false
  eventCache: Map<string, EventRow> = new Map()
  unreadCounts: UnreadCounts = { total: 0, high: 0, medium: 0, low: 0, currentField: 0 }
  listeners: Set<EventListener> = new Set()
  fieldStore: ReturnType<typeof fieldState> | null = null
  currentUserId: string | null = null
  cacheExpiryTime = 24 * 60 * 60 * 1000
  lastCheckTime: Date | null = null
  isHealthy = true
  private pollingUnsub: (() => void) | null = null

  notificationStrategies: Record<string, NotificationStrategy> = {
    [EVENT_PRIORITIES.HIGH]: {
      showPageNotifications: true,
      showSysNotification: true,
      showBrowserNotification: true,
      playSound: true,
      autoHide: false
    },
    [EVENT_PRIORITIES.MEDIUM]: {
      showPageNotifications: true,
      showSysNotification: true,
      showBrowserNotification: false,
      playSound: false,
      autoHide: 5000
    },
    [EVENT_PRIORITIES.LOW]: {
      showPageNotifications: false,
      showSysNotification: false,
      showBrowserNotification: false,
      playSound: false,
      autoHide: 3000
    }
  }

  /**
   * 統一啟動入口
   */
  async start(userId: string, callbacks: LoadEventsCallbacks = {}): Promise<void> {
    if (!userId) {
      throw new Error('缺少 userId 參數')
    }

    if (this.isInitialized && this.currentUserId === userId) {
      return
    }

    if (this._isStarting) {
      return
    }
    this._isStarting = true

    if (this.isInitialized) {
      this.destroy()
    }

    console.log('[EventService] 啟動中...')

    try {
      this.currentUserId = userId
      this.fieldStore = fieldState()

      await this.loadEventsWithCache(callbacks)
      await this.startRealtimeSubscription()

      this.isInitialized = true
      this._isStarting = false
      console.log('[EventService] 啟動完成')

      this.notifyListeners('service:initialized', null)
    } catch (error) {
      this._isStarting = false
      console.error('[EventService] 啟動失敗:', error)
      throw error
    }
  }

  /** @deprecated 請改用 start() 方法 */
  async init(userId: string): Promise<void> {
    return this.start(userId)
  }

  /** @deprecated 建議改用 loadEventsWithCache() */
  async loadRecentEventsWithRetry(maxRetries = 3): Promise<EventRow[]> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await this.loadRecentEvents()
      } catch (error) {
        const err = error as Error
        console.warn(`[EventService] 載入事件失敗 (嘗試 ${i + 1}/${maxRetries}):`, err.message)
        if (i < maxRetries - 1) {
          const delay = 1000 * Math.pow(2, i)
          console.log(`[EventService] ${delay / 1000} 秒後重試...`)
          await new Promise(r => setTimeout(r, delay))
        } else {
          console.error('[EventService] 載入事件最終失敗，已達最大重試次數')
          throw error
        }
      }
    }
    return []
  }

  async initSqlService(): Promise<void> {
    // Supabase client 不需要顯式初始化
  }

  // ==================== 鏈式查詢 API ====================

  query(): EventQueryBuilder {
    return new EventQueryBuilder()
  }

  async getById(eventId: string): Promise<EventRow | null> {
    return this.query().id(eventId).first()
  }

  // ==================== 格式轉換 ====================

  transformMqttDataToEvent(data: MqttData, priority: EventPriority): EventRow {
    return _transformMqttDataToEvent(this, data, priority)
  }

  /** @deprecated 建議改用 loadEventsWithCache() */
  async loadRecentEvents(): Promise<EventRow[]> {
    return this.loadEventsByDateRange(null, null)
  }

  /** @deprecated 建議改用 query().dateRange(start, end).execute() */
  async loadEventsByDateRange(startDate: Date | null, endDate: Date | null): Promise<EventRow[]> {
    try {
      let queryBuilder = this.query()

      if (startDate && endDate) {
        queryBuilder = queryBuilder.dateRange(startDate, endDate)
      } else {
        queryBuilder = queryBuilder.lastDays(1)
      }

      const events = await queryBuilder.execute()

      events.forEach(event => {
        event.is_read = Boolean(event.is_read)
        event.is_resolved = Boolean(event.is_resolved)
        this.eventCache.set(event.id, event)
      })

      this.lastCheckTime = new Date()

      return events
    } catch (error) {
      console.error('[EventService] 載入歷史事件失敗:', error)
      throw error
    }
  }

  async addEvent(eventData: EventRow): Promise<void> {
    try {
      this.eventCache.set(eventData.id, eventData)
      await this.saveEventToDatabase(eventData)
      this.updateUnreadCountsForEvent(eventData)
      this.notifyListeners('event:added', eventData)
      console.log('[EventService] 新增事件（已存資料庫）:', eventData.id)
    } catch (error) {
      console.error('[EventService] 新增事件失敗:', error)
      // 從 cache 移除失敗的事件
      this.eventCache.delete(eventData.id)
      throw error
    }
  }

  async saveEventToDatabase(eventData: EventRow): Promise<void> {
    const data = {
      id: eventData.id,
      type: eventData.type,
      priority: eventData.priority,
      source: eventData.source,
      title: eventData.title,
      content: eventData.content,
      organization_id: eventData.organization_id,
      organization_name: eventData.organization_name,
      field_id: eventData.field_id,
      field_name: eventData.field_name,
      system_id: eventData.system_id,
      system_name: eventData.system_name,
      device_id: eventData.device_id,
      device_name: eventData.device_name,
      event_level: eventData.event_level,
      source_detail: eventData.source_detail,
      location: eventData.location || null,
      temperature: eventData.temperature,
      extra_data: eventData.extra_data || null,
      attachments: eventData.attachments || null,
      actions: eventData.actions || null,
      is_read: eventData.is_read,
      is_resolved: eventData.is_resolved
    }

    const { error } = await supabase.from('events').insert(data)
    if (error) throw error
  }

  async markAsRead(eventId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', eventId)

      if (error) throw error

      await eventCacheService.updateEvent(eventId, { is_read: true })

      const event = this.eventCache.get(eventId)
      if (event) {
        event.is_read = true
        this.updateUnreadCountsForEvent(event, true)
      }

      this.notifyListeners('event:read', { eventId })
      eventBus.emit('event:updated', { id: eventId, changes: { is_read: true } })

      console.log('[EventService] 事件已標記為已讀:', eventId)
    } catch (error) {
      console.error('[EventService] 標記已讀失敗:', error)
    }
  }

  async updateEvent(eventId: string, updates: Partial<EventRow>): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', eventId)

      if (error) throw error

      await eventCacheService.updateEvent(eventId, updates)

      const event = this.eventCache.get(eventId)
      if (event) {
        Object.assign(event, updates)
      }

      this.notifyListeners('event:updated', { eventId, updates })
      eventBus.emit('event:updated', { id: eventId, changes: updates })

      console.log('[EventService] 事件已更新:', eventId)
    } catch (error) {
      console.error('[EventService] 更新事件失敗:', error)
      throw error
    }
  }

  async deleteEvent(eventId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', eventId)

      if (error) throw error

      this._removeEventFromCache(eventId)
      this.notifyListeners('event:deleted', { eventId })

      console.log('[EventService] 事件已軟刪除:', eventId)
    } catch (error) {
      console.error('[EventService] 刪除事件失敗:', error)
      throw error
    }
  }

  getEventsByFilter(filters: EventFilters = {}): EventRow[] {
    const events = Array.from(this.eventCache.values())

    return events.filter(event => {
      if (filters.startDate && filters.endDate) {
        const eventDate = new Date(event.created_at)
        const startDate = new Date(filters.startDate)
        const endDate = new Date(filters.endDate)
        endDate.setHours(23, 59, 59, 999)

        if (eventDate < startDate || eventDate > endDate) {
          return false
        }
      } else if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2) {
        const eventDate = new Date(event.created_at)
        const startDate = new Date(filters.dateRange[0])
        const endDate = new Date(filters.dateRange[1])
        endDate.setHours(23, 59, 59, 999)

        if (eventDate < startDate || eventDate > endDate) {
          return false
        }
      }

      if (filters.type && event.type !== filters.type) return false
      if (filters.types && Array.isArray(filters.types) && filters.types.length > 0) {
        if (!filters.types.includes(event.type)) return false
      }
      if (filters.priority && event.priority !== filters.priority) return false
      if (filters.source && event.source !== filters.source) return false
      if (filters.event_level && event.event_level !== filters.event_level) return false
      if (filters.organization_id && event.organization_id !== filters.organization_id) return false
      if (filters.field_id && event.field_id !== filters.field_id) return false
      if (filters.field_ids && Array.isArray(filters.field_ids) && filters.field_ids.length > 0) {
        if (event.event_level === 'organization' || event.event_level === 'system') {
          // 允許通過
        } else {
          if (!event.field_id || !filters.field_ids.includes(event.field_id)) {
            return false
          }
        }
      }
      if (filters.system_id && event.system_id !== filters.system_id) return false
      if (filters.device_id && event.device_id !== filters.device_id) return false
      if (filters.id && event.id !== filters.id) return false
      if (filters.is_read !== undefined && event.is_read !== filters.is_read) return false
      if (filters.is_resolved !== undefined && event.is_resolved !== filters.is_resolved) return false

      if (filters.minDurationMinutes && filters.minDurationMinutes > 0) {
        const minMinutes = filters.minDurationMinutes

        if (event.is_resolved) {
          if ((event.duration_minutes ?? 9999) < minMinutes) return false
        } else if (event.created_at) {
          const durationMs = Date.now() - new Date(event.created_at).getTime()
          if (durationMs < minMinutes * 60 * 1000) return false
        }
      }

      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase()
        return (event.title?.toLowerCase().includes(keyword) ?? false) ||
               (event.content?.toLowerCase().includes(keyword) ?? false) ||
               (event.field_name?.toLowerCase().includes(keyword) ?? false) ||
               (event.device_name?.toLowerCase().includes(keyword) ?? false)
      }

      return true
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }

  getCurrentFieldEvents(): EventRow[] {
    const currentFieldId = this.fieldStore?.selectedFieldId
    if (!currentFieldId || currentFieldId === '--none--') {
      return []
    }

    return this.getEventsByFilter({
      field_id: currentFieldId
    })
  }

  getRecentAlerts(): EventRow[] {
    return this.getEventsByFilter({
      priority: EVENT_PRIORITIES.HIGH,
      type: EVENT_TYPES.ALARM
    }).slice(0, 10)
  }

  async getUnreadCount(filters: Partial<EventFilters> = {}): Promise<number> {
    let query = this.query().unread().lastDays(7).cacheOnly()

    if (filters.priority) query = query.priority(filters.priority)
    if (filters.field_id) query = query.field(filters.field_id)
    if (filters.field_ids) query = query.fields(filters.field_ids)

    const events = await query.execute()
    return events.length
  }

  async getTotalUnreadCount(): Promise<number> {
    return this.getUnreadCount()
  }

  async getCurrentFieldUnreadCount(): Promise<number> {
    const currentFieldId = this.fieldStore?.selectedFieldId
    if (!currentFieldId || currentFieldId === '--none--') {
      return 0
    }

    return this.getUnreadCount({
      field_id: currentFieldId
    })
  }

  async updateUnreadCounts(): Promise<void> {
    try {
      const [total, high, medium, low, currentField] = await Promise.all([
        this.getTotalUnreadCount(),
        this.getUnreadCount({ priority: EVENT_PRIORITIES.HIGH }),
        this.getUnreadCount({ priority: EVENT_PRIORITIES.MEDIUM }),
        this.getUnreadCount({ priority: EVENT_PRIORITIES.LOW }),
        this.getCurrentFieldUnreadCount()
      ])

      this.unreadCounts = { total, high, medium, low, currentField }

      eventBus.emit('eventService:unreadUpdated', this.unreadCounts)
      this.notifyListeners('unread:updated', this.unreadCounts)
    } catch (error) {
      console.error('[EventService] 更新未讀計數失敗:', error)
    }
  }

  updateUnreadCountsForEvent(_event: EventRow | null, _wasRead = false): void {
    this.updateUnreadCounts()
  }

  handleNotificationStrategy(event: EventRow): void {
    const strategy = this.notificationStrategies[event.priority]
    if (!strategy) return

    eventBus.emit('eventService:notificationShow', { event, strategy })
    this.notifyListeners('notification:show', { event, strategy })
  }

  async manualCheckForNewEvents(): Promise<void> {
    await this.pollForEventChanges()
  }

  async checkForNewEvents(_forceCheck = false): Promise<void> {
    // 統一使用 pollForEventChanges，避免重複通知
    await this.pollForEventChanges()
  }

  // ==================== 輪詢模式（取代 Supabase Realtime） ====================

  async startRealtimeSubscription(): Promise<void> {
    this.stopRealtimeSubscription()

    // 使用 pollingManager 每 10s 增量檢查新/更新/刪除的事件
    this.pollingUnsub = pollingManager.register({
      key: 'events',
      fetchFn: async () => {
        await this.pollForEventChanges()
      },
      intervalMs: 10_000,
      pauseWhenHidden: false, // 事件通知即使背景也要收
      immediate: false, // 初始資料由 start() 載入，不需重複
    })
  }

  stopRealtimeSubscription(): void {
    if (this.pollingUnsub) {
      this.pollingUnsub()
      this.pollingUnsub = null
    }
  }

  /**
   * 增量輪詢：先用輕量查詢檢查有無變動，有才拉完整資料。
   * field_id 在 client-side 過濾（與舊 Realtime 行為一致，避免超長 URL）
   */
  private async pollForEventChanges(): Promise<void> {
    if (!this.isInitialized || !this.currentUserId) return

    try {
      const since = this.lastCheckTime || new Date(Date.now() - 24 * 60 * 60 * 1000)

      // Step 1: 輕量查詢 — 只拿 max(updated_at)，確認有無變動
      const { data: peekData, error: peekError } = await supabase
        .from('events')
        .select('updated_at')
        .gt('updated_at', since.toISOString())
        .order('updated_at', { ascending: false })
        .limit(1)

      if (peekError) {
        console.error('[EventService] 輪詢 peek 失敗:', peekError)
        if (this.isHealthy) {
          this.isHealthy = false
          this.notifyListeners('health:degraded', null)
        }
        return
      }

      // 沒有任何變動，跳過
      if (!peekData || peekData.length === 0) {
        if (!this.isHealthy) {
          this.isHealthy = true
          this.notifyListeners('health:recovered', null)
        }
        this.lastCheckTime = new Date()
        return
      }

      // Step 2: 有變動 — 拉完整資料（不帶 field_id 過濾，避免超長 URL）
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .gt('updated_at', since.toISOString())
        .order('updated_at', { ascending: true })
        .limit(200)

      if (error) {
        console.error('[EventService] 輪詢事件失敗:', error)
        if (this.isHealthy) {
          this.isHealthy = false
          this.notifyListeners('health:degraded', null)
        }
        return
      }

      if (!this.isHealthy) {
        this.isHealthy = true
        this.notifyListeners('health:recovered', null)
      }

      const events = (data || []) as EventRow[]
      const fieldIds = this.getUserFieldIds()
      let hasNewEvents = false

      for (const event of events) {
        // client-side field_id 過濾（與舊 Realtime handler 行為一致）
        if (event.field_id && fieldIds.length > 0 && !fieldIds.includes(event.field_id)) {
          continue
        }

        event.is_read = Boolean(event.is_read)
        event.is_resolved = Boolean(event.is_resolved)

        const cached = this.eventCache.get(event.id)

        // 軟刪除偵測
        if (event.deleted_at) {
          if (cached) {
            this._removeEventFromCache(event.id)
            this.notifyListeners('event:deleted', { eventId: event.id })
            eventBus.emit('event:deleted', { id: event.id })
          }
          continue
        }

        if (!cached) {
          // 新事件
          this.eventCache.set(event.id, event)
          eventCacheService.saveEvents([event]).catch(err => {
            console.warn('[EventService] 儲存事件到 IndexedDB 失敗:', err)
          })
          this.notifyListeners('event:added', event)
          eventBus.emit('event:added', event)
          this.handleNotificationStrategy(event)
          hasNewEvents = true
        } else {
          // 更新事件
          const wasRead = cached.is_read
          this.eventCache.set(event.id, event)
          eventCacheService.updateEvent(event.id, event).catch(err => {
            console.warn('[EventService] 更新 IndexedDB 事件失敗:', err)
          })
          this.notifyListeners('event:updated', event)
          eventBus.emit('event:updated', { id: event.id, changes: event, old: cached })

          if (!wasRead && event.is_read) {
            this.updateUnreadCountsForEvent(event, true)
          }
        }
      }

      if (hasNewEvents) {
        await this.updateUnreadCounts()
      }

      // 用回傳資料的最大 updated_at 推進游標
      if (events.length > 0) {
        const lastEvent = events[events.length - 1]
        if (lastEvent.updated_at) {
          this.lastCheckTime = new Date(lastEvent.updated_at)
        }
      } else {
        this.lastCheckTime = new Date()
      }
    } catch (error) {
      console.error('[EventService] 輪詢事件變更失敗:', error)
    }
  }

  private _removeEventFromCache(eventId: string): void {
    this.eventCache.delete(eventId)
    eventCacheService.deleteEvent(eventId).catch(err => {
      console.warn('[EventService] 刪除 IndexedDB 事件失敗:', err)
    })
  }

  addEventListener(listener: EventListener): void {
    this.listeners.add(listener)
  }

  removeEventListener(listener: EventListener): void {
    this.listeners.delete(listener)
  }

  notifyListeners(eventType: string, data: unknown = null): void {
    this.listeners.forEach(listener => {
      try {
        listener(eventType, data)
      } catch (error) {
        console.error('[EventService] 監聽器執行錯誤:', error)
      }
    })
  }

  // ==================== 格式轉換方法 ====================

  createStandardEvent(options: Partial<EventRow> = {}): EventRow {
    return _createStandardEvent(this, options)
  }

  convertEventFormat(event: EventRow, targetFormat: 'display' | 'standard'): DisplayEvent | EventRow {
    return _convertEventFormat(this, event, targetFormat)
  }

  convertEventsToFormat(events: EventRow[], targetFormat: 'display'): DisplayEvent[]
  convertEventsToFormat(events: EventRow[], targetFormat: 'standard'): EventRow[]
  convertEventsToFormat(events: EventRow[], targetFormat: 'display' | 'standard'): (DisplayEvent | EventRow)[] {
    if (targetFormat === 'display') return convertEventsToDisplayFormat(this, events)
    return convertEventsToStandardFormat(this, events)
  }

  convertFromTestEventFormat(testEvent: EventRow): EventRow {
    return _convertEventFormat(this, testEvent, 'standard') as EventRow
  }

  convertFromMqttFormat(mqttData: MqttData, priority: EventPriority): EventRow {
    return this.transformMqttDataToEvent(mqttData, priority)
  }

  generateDefaultActions(event: EventRow): Array<{ id: string; label: string; type: string }> {
    return _generateDefaultActions(event)
  }

  mapEventTypeToFieldEventType(eventType: EventType): string {
    return _mapEventTypeToFieldEventType(eventType)
  }

  validateEventFormat(event: EventRow): boolean {
    return _validateEventFormat(event)
  }

  // ==================== 標籤/顏色方法 ====================

  getEventTypeLabel(type: EventType): string { return getEventTypeLabel(type) }
  getPriorityLabel(priority: EventPriority): string { return getPriorityLabel(priority) }
  getSourceLabel(source: EventSource): string { return getSourceLabel(source) }
  getEventTypeColor(type: EventType): string { return getEventTypeColor(type) }
  getPriorityColor(priority: EventPriority): string { return getPriorityColor(priority) }
  getIncidentLevelLabel(level: string): string { return getIncidentLevelLabel(level as import('./types').IncidentLevel) }
  getIncidentStatusLabel(status: string): string { return getIncidentStatusLabel(status as import('./types').IncidentStatus) }
  getRiskTypeLabel(riskType: RiskType): string { return getRiskTypeLabel(riskType) }
  getRiskLevelLabel(level: RiskLevel): string { return getRiskLevelLabel(level) }

  // ==================== 緊急事件方法 ====================

  createIncidentEvent(data: Parameters<typeof _createIncidentEvent>[1]): EventRow {
    return _createIncidentEvent(this, data)
  }

  getIncidentEvents(options: EventFilters = {}): EventRow[] {
    return _getIncidentEvents(this, options)
  }

  getIncidentsByLevel(level: string): EventRow[] {
    return _getIncidentsByLevel(this, level)
  }

  getIncidentsByStatus(status: string): EventRow[] {
    return _getIncidentsByStatus(this, status)
  }

  async updateIncidentStatus(eventId: string, status: string, note = ''): Promise<EventRow | null> {
    return _updateIncidentStatus(this, eventId, status, note)
  }

  async addIncidentResponseLog(eventId: string, log: { type?: string; content: string; user?: string }): Promise<EventRow | null> {
    return _addIncidentResponseLog(this, eventId, log)
  }

  // ==================== 風險事件方法 ====================

  createRiskEvent(data: Parameters<typeof _createRiskEvent>[1]): EventRow {
    return _createRiskEvent(this, data)
  }

  getRiskEvents(options: EventFilters = {}): EventRow[] {
    return _getRiskEvents(this, options)
  }

  getRiskEventsByType(riskType: RiskType): EventRow[] {
    return _getRiskEventsByType(this, riskType)
  }

  getRiskEventsByLevel(level: RiskLevel): EventRow[] {
    return _getRiskEventsByLevel(this, level)
  }

  getRiskStats(): { total: number; byType: Record<string, number>; byLevel: Record<string, number> } {
    return _getRiskStats(this)
  }

  getIncidentStats(): { total: number; byLevel: Record<string, number>; byStatus: Record<string, number> } {
    return _getIncidentStats(this)
  }

  // ==================== 後端聚合統計方法 ====================

  async getEventStats(startDate: Date | string, endDate: Date | string, fieldIds: string[] | null = null, organizationId: string | null = null): Promise<unknown> {
    return _getEventStats(startDate, endDate, fieldIds, organizationId)
  }

  async getEventTrend(startDate: Date | string, endDate: Date | string, fieldIds: string[] | null = null, organizationId: string | null = null): Promise<unknown[]> {
    return _getEventTrend(startDate, endDate, fieldIds, organizationId)
  }

  async getFieldEventRanking(startDate: Date | string, endDate: Date | string, fieldIds: string[] | null = null, organizationId: string | null = null, limit = 8): Promise<unknown[]> {
    return _getFieldEventRanking(startDate, endDate, fieldIds, organizationId, limit)
  }

  async getTodayEventStats(fieldIds: string[] | null = null, organizationId: string | null = null, userId: string | null = null): Promise<unknown> {
    return _getTodayEventStats(this, fieldIds, organizationId, userId)
  }

  async getDashboardStats(days = 7, fieldIds: string[] | null = null, organizationId: string | null = null, minDurationMinutes = 0): Promise<EventDashboardResponse> {
    return _getDashboardStats(this, days, fieldIds, organizationId, minDurationMinutes)
  }

  async getDashboardStatsWithDurationFilter(startDate: Date, endDate: Date, fieldIds: string[] | null, organizationId: string | null, minDurationMinutes: number, days: number): Promise<EventDashboardResponse> {
    return _getDashboardStatsWithDurationFilter(this, startDate, endDate, fieldIds, organizationId, minDurationMinutes, days)
  }

  groupBy(events: EventRow[], field: keyof EventRow): Record<string, number> {
    return _groupBy(events, field)
  }

  calculateTrend(events: EventRow[], startDate: Date, endDate: Date): Array<{ date: string; total: number; resolved: number }> {
    return _calculateTrend(events, startDate, endDate)
  }

  calculateFieldRanking(events: EventRow[], limit = 8): Array<{ id: string; name: string; count: number; unresolved: number }> {
    return _calculateFieldRanking(events, limit)
  }

  calculateFlowData(events: EventRow[]): Record<string, number> {
    return _calculateFlowData(events)
  }

  // ==================== 漸進式載入（委派到 eventSync） ====================

  getUserFieldIds(): string[] {
    return _getUserFieldIds(this)
  }

  async loadEventsWithCache(callbacks: LoadEventsCallbacks = {}): Promise<void> {
    await _loadEventsWithCache(this, callbacks)
    await this.updateUnreadCounts()
  }

  calculateSyncRange(meta: SyncCacheMeta): SyncRange {
    return _calculateSyncRange(meta)
  }

  async backwardSync(range: SyncRange, onProgress: SyncProgressCallback | undefined): Promise<void> {
    return _backwardSync(this, range, onProgress)
  }

  async incrementalSync(range: SyncRange, onProgress: SyncProgressCallback | undefined): Promise<void> {
    return _incrementalSync(this, range, onProgress)
  }

  async firstSync(onProgress: SyncProgressCallback | undefined): Promise<void> {
    return _firstSync(this, onProgress)
  }

  destroy(): void {
    this.stopRealtimeSubscription()

    this.eventCache.clear()
    this.listeners.clear()
    this.unreadCounts = { total: 0, high: 0, medium: 0, low: 0, currentField: 0 }

    this.isInitialized = false
    this.lastCheckTime = null
    this.currentUserId = null
    this.fieldStore = null
  }
}

// 建立單例
const eventService = new EventService()
export default eventService
